import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Field, Panel, StatusBadge, formatDateTime } from "@/components/admin/ui";
import { getSolutionIcon } from "@/lib/solutionIcons";
import SolutionOrganizationsTab from "@/components/admin/solutions/SolutionOrganizationsTab";
import SolutionCapabilitiesTab from "@/components/admin/solutions/SolutionCapabilitiesTab";

const TABS = ["Overview", "Capabilities", "Organizations"];

export default function AdminSolutionManage() {
  const { solutionId } = useParams();
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Overview");
  const [statusSaving, setStatusSaving] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: resp } = await axios.get(`${API}/admin/solutions/${solutionId}`, { withCredentials: true });
      setData(resp);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API, solutionId]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    if (!data) return;
    const next = data.solution.status === "Active" ? "Inactive" : "Active";
    setStatusSaving(true);
    try {
      await axios.patch(`${API}/admin/solutions/${solutionId}/status`, { status: next }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setStatusSaving(false);
    }
  };

  if (error && !data) {
    return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="solution-manage-error">{error}</p>;
  }
  if (!data) {
    return <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="solution-manage-loading">Loading solution…</div>;
  }

  const { solution } = data;
  const Icon = getSolutionIcon(solution.icon);

  return (
    <div className="space-y-6" data-testid="solution-manage-page">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
        <Link to="/admin/solutions" data-testid="solution-manage-breadcrumb" className="hover:text-blue-600 dark:hover:text-blue-400">Solutions</Link>
        <ChevronRight size={14} />
        <span className="text-slate-900 dark:text-white">{solution.name}</span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-50 dark:bg-blue-500/10"><Icon size={26} className="text-[#0052FF] dark:text-blue-400" /></span>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white" data-testid="solution-manage-name">{solution.name}</h1>
              {solution.is_core && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:bg-violet-500/10 dark:text-violet-400">Core Solution</span>}
            </div>
            <p className="mt-1 max-w-lg text-sm text-slate-500 dark:text-slate-400">{solution.description}</p>
            <div className="mt-2"><StatusBadge status={solution.status} testId="solution-manage-status" /></div>
          </div>
        </div>
        <button type="button" onClick={toggleStatus} disabled={statusSaving} data-testid="solution-toggle-status-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
          {solution.status === "Active" ? "Deactivate Solution" : "Activate Solution"}
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto border-b border-slate-200 dark:border-white/10" data-testid="solution-manage-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`solution-tab-${t.toLowerCase()}`}
            className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-bold ${tab === t ? "border-[#0052FF] text-[#0052FF] dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
          >
            {t}{t === "Organizations" ? ` (${data.organizations_count})` : ""}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <Panel title="Solution Information">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Solution Name" value={solution.name} testId="solution-info-name" />
              <Field label="Internal Key" value={solution.slug} testId="solution-info-slug" />
              <Field label="Display Order" value={solution.display_order} testId="solution-info-order" />
              <Field label="Created" value={formatDateTime(solution.created_at)} testId="solution-info-created" />
              <Field label="Last Updated" value={formatDateTime(solution.updated_at)} testId="solution-info-updated" />
            </div>
          </Panel>
          <Panel title="Solution Summary">
            <dl className="space-y-3 text-sm" data-testid="solution-summary-panel">
              <SummaryRow label="Organizations using this solution" value={data.organizations_count} />
              <SummaryRow label="Users with access" value={data.users_with_access} />
              <SummaryRow label="Capabilities" value={(solution.capabilities || []).length} />
            </dl>
          </Panel>
        </div>
      )}

      {tab === "Capabilities" && <SolutionCapabilitiesTab solution={solution} solutionId={solutionId} API={API} onSaved={load} />}
      {tab === "Organizations" && <SolutionOrganizationsTab organizations={data.organizations} solutionId={solutionId} API={API} onSaved={load} />}
    </div>
  );
}

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">{label}</dt>
    <dd className="text-lg font-extrabold text-slate-950 dark:text-white">{value}</dd>
  </div>
);
